/**
 * One message, one tone, one icon.
 *
 * Asked for 2026-08-21, when a confirmation ("slovo čeká na zítřek") rendered in
 * the same red as a refusal: "neměla by ta message být zelená? Klidně uprav
 * messaging systém, aby tam byla i ikonka a rozdělené severity."
 *
 * There were two systems — .social-modal__msg and .settings-msg — and every site
 * decided its own tone. Two of them decided it by looking for a Czech word in the
 * text:
 *
 *     emailMsg.includes('změněn')   ? ok : error
 *     passwordMsg.includes('odeslán') ? ok : error
 *
 * which colours every success as a failure in the four languages that are not
 * Czech. A tone is something the code KNOWS when it sets the message; guessing it
 * back out of the words afterwards can only lose.
 *
 * A message value is either a string — taken as an error, which is what a bare
 * string has always meant here — or {text, tone}. Use msg() to build one.
 */

export const TONES = ["success", "error", "info"];

// Text, not an icon font or an SVG set: the game is monospace throughout and
// these sit inside a line of it. Marked aria-hidden — the tone is decoration,
// the sentence carries the meaning.
const GLYPH = { success: "✓", error: "!", info: "i" };

/** Build a message with an explicit tone. */
export function msg(text, tone = "info") {
  return { text, tone: TONES.includes(tone) ? tone : "info" };
}

export const ok = (text) => msg(text, "success");
export const err = (text) => msg(text, "error");

/**
 * Normalise any message value to {text, tone}, or null when there is nothing to
 * show. A bare string keeps its historical meaning (a refusal).
 */
export function normalize(value) {
  if (!value) return null;
  if (typeof value === "string") return { text: value, tone: "error" };
  if (typeof value === "object" && value.text) {
    return { text: value.text, tone: TONES.includes(value.tone) ? value.tone : "info" };
  }
  return null;
}

/**
 * @param {object} props
 * @param {string|{text:string,tone:string}} props.value
 * @param {string} [props.className] extra classes for the surface it sits on
 */
export default function Msg({ value, className = "" }) {
  const m = normalize(value);
  if (!m) return null;
  return (
    <p className={`msg msg--${m.tone} ${className}`.trim()} role={m.tone === "error" ? "alert" : "status"}>
      <span className="msg__glyph" aria-hidden="true">{GLYPH[m.tone]}</span>
      <span className="msg__text">{m.text}</span>
    </p>
  );
}
